/*---------------------------------------------------------------------------------------------
 *  Copyright (c) Microsoft Corporation. All rights reserved.
 *  Licensed under the MIT License. See License.txt in the project root for license information.
 *--------------------------------------------------------------------------------------------*/

import { Dimension } from 'vs/base/browser/dom';
import { IDisposable } from 'vs/base/common/lifecycle';
import { QuickOpenModel } from 'vs/base/parts/quickopen/browser/quickOpenModel';
import { QuickOpenWidget } from 'vs/base/parts/quickopen/browser/quickOpenWidget';
import { IAutoFocus } from 'vs/base/parts/quickopen/common/quickOpen';
import { ICodeEditor, IOverlayWidget, IOverlayWidgetPosition, OverlayWidgetPositionPreference } from 'vs/editor/browser/editorBrowser';
import { foreground } from 'vs/platform/theme/common/colorRegistry';
import { attachQuickOpenStyler } from 'vs/platform/theme/common/styler';
import { IThemeService } from 'vs/platform/theme/common/themeService';

export interface IQuickOpenEditorWidgetOptions {
	inputAriaLabel: string;
}

export class QuickOpenEditorWidget implements IOverlayWidget {

	private static readonly ID = 'editor.contrib.quickOpenEditorWidget';

	private readonly codeEditor: ICodeEditor;
	private readonly themeService: IThemeService;
	private visible: boolean | undefined;
	private quickOpenWidget: QuickOpenWidget;
	private domNode: HTMLElement;
	private styler: IDisposable;

	constructor(codeEditor: ICodeEditor, onOk: () => void, onCancel: () => void, onType: (value: string) => void, configuration: IQuickOpenEditorWidgetOptions, themeService: IThemeService) {
		this.codeEditor = codeEditor;
		this.themeService = themeService;
		this.visible = false;

		this.domNode = document.createElement('div');

		this.quickOpenWidget = new QuickOpenWidget(
			this.domNode,
			{
				onOk: onOk,
				onCancel: onCancel,
				onType: onType
			}, {
			inputPlaceHolder: undefined,
			inputAriaLabel: configuration.inputAriaLabel,
			keyboardSupport: true
		}
		);
		this.styler = attachQuickOpenStyler(this.quickOpenWidget, this.themeService, {
			pickerGroupForeground: foreground
		});

		this.quickOpenWidget.create();
		this.codeEditor.addOverlayWidget(this);
	}

	setInput(model: QuickOpenModel, focus: IAutoFocus): void {
		this.quickOpenWidget.setInput(model, focus);
	}

	getId(): string {
		return QuickOpenEditorWidget.ID;
	}

	getDomNode(): HTMLElement {
		return this.domNode;
	}

	destroy(): void {
		this.codeEditor.removeOverlayWidget(this);
		this.quickOpenWidget.dispose();
		this.styler.dispose();
	}

	show(value: string): void {
		this.visible = true;

		const editorLayout = this.codeEditor.getLayoutInfo();
		if (editorLayout) {
			this.quickOpenWidget.layout(new Dimension(editorLayout.width, editorLayout.height));
		}

		this.quickOpenWidget.show(value);
		this.codeEditor.layoutOverlayWidget(this);
	}

	getPosition(): IOverlayWidgetPosition | null {
		if (this.visible) {
			return {
				preference: OverlayWidgetPositionPreference.TOP_CENTER
			};
		}

		return null;
	}
}
